import { CameraFilled } from '@ant-design/icons';
import {
  BackBar,
  cubicMeters,
  dirhams,
  fullDate,
  IdTag,
  isGeneratedMeterNo,
  METER_STATUS_LABELS,
  monthYear,
  PhotoView,
} from '@horof/shared';
import { Alert, Button, Result, Skeleton, Tag } from 'antd';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from 'urql';
import { MeterPhotoDialog } from './MeterPhotoDialog';
import { MeterDetailsDocument } from './meters-data';

/**
 * A meter, to look up; its photo can be taken on site. Editing, status
 * changes and the round's order are done on the desk (docs/changes/41–42).
 */
export function MeterPage() {
  const { meterId = '' } = useParams();
  const [{ data, error }] = useQuery({
    query: MeterDetailsDocument,
    variables: { id: meterId },
  });
  const [addingPhoto, setAddingPhoto] = useState(false);

  if (!data) {
    return (
      <div className="page">
        <BackBar to="/meters" label="العدادات" />
        {error ? (
          <Alert type="error" showIcon message="تعذّر تحميل العداد" />
        ) : (
          <Skeleton active paragraph={{ rows: 6 }} />
        )}
      </div>
    );
  }
  const meter = data.meter;
  if (!meter) {
    return (
      <div className="page">
        <BackBar to="/meters" label="العدادات" />
        <Result status="404" title="العداد غير موجود" />
      </div>
    );
  }
  const bills = meter.recentBills.nodes.flatMap((b) => (b ? [b] : []));
  const statusChanges = meter.meterStatusChanges.nodes.flatMap((c) =>
    c ? [c] : [],
  );

  return (
    <div className="page">
      <BackBar to="/meters" label="العدادات" />
      <section className="card">
        <h1 className="member-title">العداد {meter.label}</h1>
        <dl className="details">
          <dt>العضو</dt>
          <dd>
            {meter.member && (
              <Link to={`/members/${meter.member.id}`}>
                {meter.member.fullName}
              </Link>
            )}
          </dd>
          <dt>الرقم</dt>
          <dd>
            {isGeneratedMeterNo(meter.meterNo) ? (
              <span className="field-hint">بدون رقم</span>
            ) : (
              <span className="ltr-num">{meter.meterNo}</span>
            )}
          </dd>
          <dt>العنوان</dt>
          <dd>{meter.meterAddress}</dd>
          <dt>الحالة</dt>
          <dd>
            <Tag
              color={meter.meterStatus === 'CONNECTED' ? 'green' : 'default'}
            >
              {METER_STATUS_LABELS[meter.meterStatus]}
            </Tag>
            {meter.statusReason && (
              <div className="field-hint">
                {meter.statusReason}
                {meter.statusChangedAt &&
                  ` (${fullDate(meter.statusChangedAt)})`}
              </div>
            )}
          </dd>
          <dt>منذ</dt>
          <dd>{fullDate(meter.connectedAt)}</dd>
        </dl>
        <div className="field-hint">
          تعديل العداد وتغيير حالته من الحاسوب («مكتب الجمعية»).
        </div>
      </section>

      <section className="card">
        <div className="field-label">صورة العداد</div>
        {meter.photo ? (
          <PhotoView
            url={meter.photo.url}
            alt={`صورة العداد ${meter.label}`}
            caption={`${fullDate(meter.photo.createdAt)}${meter.photo.uploadedBy ? ` · ${meter.photo.uploadedBy}` : ''}`}
          />
        ) : (
          <div className="field-hint">لا صورة لهذا العداد.</div>
        )}
        <Button size="large" block onClick={() => setAddingPhoto(true)}>
          {meter.photo ? 'تغيير الصورة' : 'إضافة صورة'}
        </Button>
      </section>

      {statusChanges.length > 0 && (
        <section className="card">
          <div className="field-label">تاريخ الحالة</div>
          <ul className="plain-list">
            {statusChanges.map((c) => (
              <li key={c.id}>
                <strong>
                  {c.fromStatus
                    ? `${METER_STATUS_LABELS[c.fromStatus]} ← `
                    : ''}
                  {METER_STATUS_LABELS[c.toStatus]}
                </strong>
                <div className="field-hint">
                  {fullDate(c.changedOn)} · {c.reason}
                  {c.reading !== null && ` · القراءة ${c.reading}`}
                  {c.meterByReplacedBy &&
                    ` · العداد الجديد: ${c.meterByReplacedBy.label}`}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card">
        <div className="field-label">آخر القراءات</div>
        {bills.length === 0 && <div className="field-hint">لا قراءة بعد.</div>}
        <ul className="plain-list">
          {bills.map((b) => (
            <li key={b.id}>
              <Link to={`/bills/${b.id}`} className="meter-line row-link">
                <span>
                  <strong>
                    {monthYear(b.billMonth)}
                    {b.readingPhoto && (
                      <CameraFilled
                        className="photo-icon"
                        aria-label="مع صورة العداد"
                      />
                    )}
                  </strong>
                  <div className="field-hint">
                    القراءة <span className="ltr-num">{b.currentReading}</span>{' '}
                    · {cubicMeters(b.consumption)}
                  </div>
                </span>
                <span>
                  {dirhams(b.billTotal)}
                  {b.billStatus !== 'PAID' && (
                    <div>
                      <Tag
                        color={b.billStatus === 'OVERDUE' ? 'red' : 'orange'}
                      >
                        {b.billStatus === 'OVERDUE' ? 'متأخرة' : 'غير مؤداة'}
                      </Tag>
                    </div>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
      {addingPhoto && (
        <MeterPhotoDialog
          meterId={meter.id}
          hasPhoto={Boolean(meter.photo)}
          onClose={() => setAddingPhoto(false)}
        />
      )}
      <IdTag id={meter.id} label="معرّف العداد" />
    </div>
  );
}
